import { motion } from "framer-motion";
import { Headphones, ServerCog, Keyboard, LifeBuoy, PhoneOutgoing, ArrowUpRight } from "lucide-react";
import Reveal, { EASE } from "@/components/Reveal";
import { scrollToSection } from "@/App";

const services = [
  {
    icon: Headphones,
    title: "Customer Support",
    desc: "Voice, chat and email support that sounds like your brand — not a script. Trained agents, QA on every queue, CSAT you can brag about.",
    span: "md:col-span-4",
    testid: "service-card-customer-support",
  },
  {
    icon: ServerCog,
    title: "Back-Office Operations",
    desc: "Order processing, billing, admin and the operational grind — handled end to end.",
    span: "md:col-span-2",
    testid: "service-card-back-office",
  },
  {
    icon: Keyboard,
    title: "Data Entry & Processing",
    desc: "High-volume, high-accuracy data work with double-key verification and 99.5%+ accuracy guarantees.",
    span: "md:col-span-2",
    testid: "service-card-data-entry",
  },
  {
    icon: LifeBuoy,
    title: "Technical Support",
    desc: "Tier-1 and Tier-2 helpdesk for SaaS and product companies, trained on your stack.",
    span: "md:col-span-2",
    testid: "service-card-technical-support",
  },
  {
    icon: PhoneOutgoing,
    title: "Sales & Telemarketing",
    desc: "Outbound campaigns, lead qualification and appointment setting that fill your pipeline.",
    span: "md:col-span-2",
    testid: "service-card-sales",
  },
];

const Services = () => (
  <section id="services" data-testid="services-section" className="py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <Reveal>
        <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-sky-400 md:text-sm">
          01 — What We Do
        </p>
        <h2 className="max-w-3xl font-display text-4xl font-bold tracking-tight md:text-6xl">
          Every operation you'd rather not run.
        </h2>
      </Reveal>

      <div className="mt-16 grid gap-0 border border-white/10 md:grid-cols-6">
        {services.map((s, i) => (
          <motion.article
            key={s.title}
            data-testid={s.testid}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.08, ease: EASE }}
            className={`group relative -mt-px -ml-px border border-white/10 bg-[#0D1424] p-8 transition-colors duration-500 hover:bg-[#111827] md:p-10 ${s.span}`}
          >
            <s.icon className="h-8 w-8 text-sky-400 transition-transform duration-500 group-hover:scale-110 group-hover:text-sky-300" />
            <h3 className="mt-6 font-display text-2xl font-bold tracking-tight md:text-3xl">{s.title}</h3>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400 md:text-base">{s.desc}</p>
            <span className="pointer-events-none absolute right-6 top-6 h-2 w-2 rounded-full bg-sky-400 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
          </motion.article>
        ))}

        <motion.button
          data-testid="services-custom-cta"
          onClick={scrollToSection("#contact")}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="group -mt-px -ml-px flex w-full items-center justify-between border border-white/10 bg-sky-400 px-8 py-8 text-left transition-colors duration-500 hover:bg-white md:col-span-6 md:px-10"
        >
          <span className="font-display text-xl font-bold text-[#0A0F1C] md:text-2xl">
            Need something custom? We build teams around your workflow.
          </span>
          <ArrowUpRight className="h-7 w-7 shrink-0 text-[#0A0F1C] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </motion.button>
      </div>
    </div>
  </section>
);

export default Services;
